import { UserDTOInternalRolesEnum } from '@reposit/api-client';
import { Formik, FormikProps } from 'formik';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import * as Yup from 'yup';
import Button from '../../components/Button';
import FlashMessage from '../../components/FlashMessage';
import FieldWithLabel from '../../components/FormFields/FieldWithLabel';
import Input from '../../components/FormFields/Input';
import TextArea from '../../components/FormFields/TextArea';
import { FullPageLoader } from '../../components/Loading';
import Modal from '../../components/Modal';
import RepositCard from '../../components/Reposit/RepositCard';
import { Header3, P2, P3 } from '../../components/Typography';
import { hasInternalRole } from '../../redux/account/account.selectors';
import { createLoadingSelector } from '../../redux/loading/loading.selector';
import {
  clearUploadedOrganizationBrandingLogo,
  DELETE_ORGANIZATION_BRANDING_STORE_KEY,
  deleteOrganizationBrandingRequested,
  FETCH_ORGANIZATION_BRANDING_STORE_KEY,
  fetchOrganizationBrandingRequested,
  PREVIEW_ORGANIZATION_BRANDING_EMAIL_STORE_KEY,
  UPLOAD_ORGANIZATION_BRANDING_LOGO_STORE_KEY,
  UPSERT_ORGANIZATION_BRANDING_STORE_KEY,
  uploadOrganizationBrandingLogoRequested,
  upsertOrganizationBrandingRequested,
} from '../../redux/organization-branding/organization-branding.actions';
import {
  getHasFetchedOrganizationBranding,
  getOrganizationBranding,
  getOrganizationBrandingEmailPreview,
  getOrganizationBrandingEmailPreviewError,
  getUploadedOrganizationBrandingLogo,
  getUploadedOrganizationBrandingLogoError,
} from '../../redux/organization-branding/organization-branding.selectors';
import { AppState } from '../../redux/root.reducer';
import { getOrganization } from '../../redux/selectors/organization.selectors';
import { FLASH_MESSAGE_TIMEOUT, useFlashMessage } from '../FlashMessage';
import OrganizationBrandingEmailPreview from './OrganizationBrandingEmailPreview';
import OrganizationBrandingReadOnlySummary from './OrganizationBrandingReadOnlySummary';

interface OrganizationBrandingProps {
  organizationId: string;
}

interface OrganizationBrandingFormValues {
  friendlyOrganizationName: string;
  emailFooterText: string;
}

interface DeleteOrganizationBrandingModalProps {
  isDeleting: boolean;
  onDismiss: () => void;
  onDelete: () => void;
}

interface SelectedLogo {
  file: File;
  profileLogoKey: string;
}

interface LogoFileError {
  message: string;
  profileLogoKey: string;
}

const ACCEPTED_LOGO_CONTENT_TYPES = ['image/png', 'image/jpeg'];
const ACCEPTED_LOGO_FILE_TYPES = '.png,.jpg,.jpeg,image/png,image/jpeg';

const PageShell = styled.div`
  box-sizing: border-box;
  margin: 0 auto;
  max-width: 1280px;
  padding: 0 16px;
  width: 100%;
`;

const BrandingContent = styled.div`
  align-items: start;
  display: grid;
  gap: 18px;
  grid-template-columns: minmax(320px, 0.72fr) minmax(520px, 1.28fr);

  &.preview-only {
    grid-template-columns: minmax(0, 1fr);
  }

  @media (max-width: 1000px) {
    grid-template-columns: 1fr;
  }
`;

const BrandingFormPane = styled.div`
  background: ${(props) => props.theme.colors.bgAccent};
  border: 1px solid rgba(58, 49, 94, 0.1);
  border-radius: 8px;
  min-width: 0;
  padding: 16px;
`;

const BrandingPreviewPane = styled.div`
  min-width: 0;
`;

const ControlBar = styled.div`
  align-items: center;
  background: #fff;
  border: 1px solid rgba(58, 49, 94, 0.1);
  border-radius: 8px;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  margin: 0 0 18px;
  padding: 12px 14px;

  @media (max-width: 720px) {
    align-items: stretch;
    flex-direction: column;
  }
`;

const ControlBarMeta = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  min-width: 0;
`;

const ControlBarActions = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;

  @media (max-width: 720px) {
    justify-content: flex-start;
  }
`;

const StatusChip = styled.span`
  align-items: center;
  background: rgba(64, 201, 162, 0.12);
  border: 1px solid rgba(64, 201, 162, 0.35);
  border-radius: 999px;
  color: ${(props) => props.theme.colors.positive};
  display: inline-flex;
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 0.75em;
  font-weight: 700;
  line-height: 1.2;
  padding: 6px 10px;

  &.draft,
  &.unsaved {
    background: rgba(255, 90, 31, 0.1);
    border-color: rgba(255, 90, 31, 0.28);
    color: ${(props) => props.theme.colors.warning};
  }

  &.saving {
    background: rgba(102, 94, 131, 0.1);
    border-color: rgba(102, 94, 131, 0.22);
    color: ${(props) => props.theme.colors.body2};
  }
`;

const SectionTitle = styled(P3)`
  color: ${(props) => props.theme.colors.body};
  font-weight: 700;
  letter-spacing: 0.08em;
  margin: 0 0 10px;
  text-transform: uppercase;
`;

const FieldStack = styled.div`
  display: grid;
  gap: 12px;

  textarea {
    min-height: 124px;
    padding: 14px 16px;
  }
`;

const LogoPanel = styled.div`
  background: #fff;
  border: 1px solid rgba(58, 49, 94, 0.1);
  border-radius: 8px;
  padding: 14px;
`;

const LogoPanelHeader = styled.div`
  align-items: baseline;
  display: flex;
  gap: 8px;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const LogoLabel = styled(P3)`
  color: ${(props) => props.theme.colors.body};
  font-weight: 700;
  margin: 0;
`;

const LogoFormat = styled(P3)`
  color: ${(props) => props.theme.colors.placeholder};
  margin: 0;
`;

const LogoPreview = styled.div`
  align-items: center;
  background: ${(props) => props.theme.colors.bgAccent};
  border: 1px dashed rgba(58, 49, 94, 0.24);
  border-radius: 6px;
  display: flex;
  justify-content: center;
  min-height: 92px;
  overflow: hidden;
  padding: 12px;

  img {
    display: block;
    max-height: 68px;
    max-width: 100%;
    object-fit: contain;
  }
`;

const LogoPlaceholder = styled(P3)`
  color: ${(props) => props.theme.colors.placeholder};
  font-weight: 600;
  margin: 0;
  text-align: center;
`;

const LogoPanelFooter = styled.div`
  align-items: center;
  display: flex;
  gap: 10px;
  justify-content: space-between;
  margin-top: 10px;

  @media (max-width: 576px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const FilePicker = styled.label`
  align-items: center;
  background: ${(props) => props.theme.colors.secondaryBrand};
  border-radius: 24px;
  color: #fff;
  cursor: pointer;
  display: inline-flex;
  flex-shrink: 0;
  font-family: ${(props) => props.theme.typography.face.primary};
  font-size: 0.75em;
  font-weight: 700;
  letter-spacing: 0.8px;
  line-height: normal;
  overflow: hidden;
  padding: 0.625em 1.5em;
  position: relative;
  text-transform: uppercase;
  transition: background 0.2s;

  &:hover {
    background: ${(props) => props.theme.colors.primaryBrand};
  }
`;

const FileInput = styled.input`
  height: 1px;
  opacity: 0;
  overflow: hidden;
  position: absolute;
  width: 1px;
`;

const LogoHelpText = styled(P3)`
  color: ${(props) => props.theme.colors.body};
  font-family: ${(props) => props.theme.typography.face.secondary};
  margin: 0;
  overflow-wrap: anywhere;
`;

const SelectedFile = styled(P3)`
  margin: 8px 0 0;
`;

const ValidationError = styled(P3)`
  color: ${(props) => props.theme.colors.warning};
  margin: 8px 0 0;
`;

const QuietDeleteButton = styled.button`
  background: #fff;
  border: 1px solid rgba(232, 49, 81, 0.38);
  border-radius: 24px;
  color: ${(props) => props.theme.colors.negative};
  cursor: pointer;
  font-family: ${(props) => props.theme.typography.face.primary};
  font-size: 0.75em;
  font-weight: 700;
  letter-spacing: 0.8px;
  line-height: normal;
  padding: 0.625em 1.25em;
  text-transform: uppercase;
  transition: all 0.2s;

  &:hover {
    background: rgba(232, 49, 81, 0.08);
  }

  &:disabled {
    border-color: ${(props) => props.theme.colors.disabled};
    color: ${(props) => props.theme.colors.placeholder};
    cursor: not-allowed;
  }
`;

const DeleteModalActions = styled.div`
  padding: 12px 0 0;
  text-align: right;

  button {
    margin-left: 8px;
  }
`;

const validationSchema = Yup.object().shape({
  friendlyOrganizationName: Yup.string().trim().required('Required'),
  emailFooterText: Yup.string().trim(),
});

const DeleteOrganizationBrandingModal: React.FC<DeleteOrganizationBrandingModalProps> = ({ isDeleting, onDismiss, onDelete }) => (
  <Modal onDismiss={onDismiss}>
    <Header3 style={{ marginBottom: 6 }}>Delete organization branding?</Header3>
    <P2>This removes the saved branding profile for this organisation.</P2>
    <DeleteModalActions>
      <Button buttonType="secondary" noArrow={true} onClick={onDismiss} type="button">
        Cancel
      </Button>
      <Button buttonType="warning" disabled={isDeleting} onClick={onDelete} type="button">
        Delete
      </Button>
    </DeleteModalActions>
  </Modal>
);

const OrganizationBranding: React.FC<OrganizationBrandingProps> = ({ organizationId }) => {
  const dispatch = useDispatch();
  const canManageOrganizationBranding = useSelector((state: AppState) =>
    hasInternalRole(state, UserDTOInternalRolesEnum.MANAGEORGANIZATIONBRANDING),
  );
  const organization = useSelector(getOrganization(organizationId));
  const profile = useSelector((state: AppState) => getOrganizationBranding(state, organizationId));
  const hasFetched = useSelector((state: AppState) => getHasFetchedOrganizationBranding(state, organizationId));
  const uploadedLogo = useSelector((state: AppState) => getUploadedOrganizationBrandingLogo(state, organizationId));
  const uploadedLogoError = useSelector((state: AppState) => getUploadedOrganizationBrandingLogoError(state, organizationId));
  const emailPreview = useSelector((state: AppState) => getOrganizationBrandingEmailPreview(state, organizationId));
  const emailPreviewError = useSelector((state: AppState) => getOrganizationBrandingEmailPreviewError(state, organizationId));
  const isFetching = useSelector(createLoadingSelector([FETCH_ORGANIZATION_BRANDING_STORE_KEY]));
  const isSaving = useSelector(createLoadingSelector([UPSERT_ORGANIZATION_BRANDING_STORE_KEY]));
  const isDeleting = useSelector(createLoadingSelector([DELETE_ORGANIZATION_BRANDING_STORE_KEY]));
  const isUploadingLogo = useSelector(createLoadingSelector([UPLOAD_ORGANIZATION_BRANDING_LOGO_STORE_KEY]));
  const isPreviewingEmail = useSelector(createLoadingSelector([PREVIEW_ORGANIZATION_BRANDING_EMAIL_STORE_KEY]));
  const isMutating = isSaving || isDeleting;
  const [flashMessagePayload, dismissFlashMessage] = useFlashMessage([
    FETCH_ORGANIZATION_BRANDING_STORE_KEY,
    UPSERT_ORGANIZATION_BRANDING_STORE_KEY,
    DELETE_ORGANIZATION_BRANDING_STORE_KEY,
  ]);
  const [selectedLogo, setSelectedLogo] = useState<SelectedLogo | null>(null);
  const [logoFileError, setLogoFileError] = useState<LogoFileError | null>(null);
  const [isDeleteModalShowing, setIsDeleteModalShowing] = useState(false);
  const hasProfile = !!profile;
  const existingEmailHeaderLogoKey = profile ? profile.emailHeaderLogoKey : undefined;
  const existingEmailHeaderLogoContentType = profile ? profile.emailHeaderLogoContentType : undefined;
  const profileLogoKey = existingEmailHeaderLogoKey || '';
  const activeSelectedLogoFile = selectedLogo && selectedLogo.profileLogoKey === profileLogoKey ? selectedLogo.file : null;
  const activeSelectedLogoFileName = activeSelectedLogoFile ? activeSelectedLogoFile.name : '';
  const activeLogoFileError = logoFileError && logoFileError.profileLogoKey === profileLogoKey ? logoFileError.message : '';

  useEffect(() => {
    dispatch(fetchOrganizationBrandingRequested({ organizationId }));
  }, [dispatch, organizationId]);

  const handleLogoFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!canManageOrganizationBranding) {
        return;
      }

      const file = (event.target.files || [])[0];

      if (!file) {
        setSelectedLogo(null);
        setLogoFileError(null);
        dispatch(clearUploadedOrganizationBrandingLogo({ organizationId }));
        return;
      }

      if (!ACCEPTED_LOGO_CONTENT_TYPES.includes(file.type)) {
        setSelectedLogo(null);
        setLogoFileError({ message: 'Only PNG and JPEG logo files are supported.', profileLogoKey });
        dispatch(clearUploadedOrganizationBrandingLogo({ organizationId }));
        event.target.value = '';
        return;
      }

      setSelectedLogo({ file, profileLogoKey });
      setLogoFileError(null);
      dispatch(uploadOrganizationBrandingLogoRequested({ organizationId, logoFile: file }));
    },
    [canManageOrganizationBranding, dispatch, organizationId, profileLogoKey],
  );

  const handleSubmit = useCallback(
    (values: OrganizationBrandingFormValues) => {
      if (!canManageOrganizationBranding || isMutating || isUploadingLogo || activeLogoFileError || uploadedLogoError) {
        return;
      }

      if (!hasProfile && !uploadedLogo) {
        setLogoFileError({ message: 'Required', profileLogoKey });
        return;
      }

      dispatch(
        upsertOrganizationBrandingRequested({
          organizationId,
          friendlyOrganizationName: values.friendlyOrganizationName,
          emailFooterText: values.emailFooterText,
          existingEmailHeaderLogoKey,
          existingEmailHeaderLogoContentType,
          uploadedEmailHeaderLogoKey: uploadedLogo ? uploadedLogo.emailHeaderLogoKey : undefined,
          uploadedEmailHeaderLogoContentType: uploadedLogo ? uploadedLogo.emailHeaderLogoContentType : undefined,
        }),
      );
    },
    [
      activeLogoFileError,
      canManageOrganizationBranding,
      dispatch,
      existingEmailHeaderLogoContentType,
      existingEmailHeaderLogoKey,
      hasProfile,
      isMutating,
      isUploadingLogo,
      organizationId,
      profileLogoKey,
      uploadedLogo,
      uploadedLogoError,
    ],
  );

  if (isFetching && !hasFetched) {
    return <FullPageLoader />;
  }

  const organizationName = organization ? organization.name : '';
  const readOnlyFriendlyName = profile ? profile.friendlyOrganizationName : organizationName;
  const readOnlyFooterText = profile ? profile.emailFooterText : '';
  const readOnlyLogoUrl = profile ? profile.emailHeaderLogoUrl : '';

  return (
    <PageShell>
      {isDeleteModalShowing && canManageOrganizationBranding ? (
        <DeleteOrganizationBrandingModal
          isDeleting={isDeleting}
          onDismiss={() => setIsDeleteModalShowing(false)}
          onDelete={() => {
            if (!canManageOrganizationBranding || isMutating) {
              return;
            }

            dispatch(deleteOrganizationBrandingRequested({ organizationId }));
            setIsDeleteModalShowing(false);
          }}
        />
      ) : null}
      <Formik<OrganizationBrandingFormValues>
        enableReinitialize
        initialValues={{
          friendlyOrganizationName: profile ? profile.friendlyOrganizationName : organizationName,
          emailFooterText: profile ? profile.emailFooterText : '',
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          dirty,
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          touched,
          values,
        }: FormikProps<OrganizationBrandingFormValues>) => {
          const hasUnsavedChanges = dirty || !!activeSelectedLogoFile || !!uploadedLogo;
          const saveStateLabel = isSaving
            ? 'Saving changes...'
            : hasUnsavedChanges
              ? 'Unsaved changes'
              : hasProfile
                ? 'All changes saved'
                : 'No unsaved changes';
          const saveStateClassName = isSaving ? 'saving' : hasUnsavedChanges ? 'unsaved' : undefined;
          const profileStatusLabel = hasProfile ? 'Published profile' : 'Draft profile';
          const profileStatusClassName = hasProfile ? undefined : 'draft';
          const logoPreviewUrl = uploadedLogo ? uploadedLogo.emailHeaderLogoUrl : profile ? profile.emailHeaderLogoUrl : '';
          const logoFileLabel = activeSelectedLogoFileName
            ? activeSelectedLogoFileName
            : uploadedLogo
              ? uploadedLogo.fileName
              : profile
                ? 'Saved logo'
                : 'No logo selected';
          const logoActionLabel = logoPreviewUrl || activeSelectedLogoFileName ? 'Replace logo' : 'Upload logo';

          return (
            <form onSubmit={handleSubmit}>
              <RepositCard
                title="Organisation Branding"
                flashMessage={
                  flashMessagePayload ? (
                    <FlashMessage
                      payload={flashMessagePayload}
                      onDismiss={dismissFlashMessage}
                      timeRemaining={FLASH_MESSAGE_TIMEOUT}
                    />
                  ) : undefined
                }
              >
                {canManageOrganizationBranding ? (
                  <ControlBar>
                    <ControlBarMeta>
                      <StatusChip className={profileStatusClassName}>{profileStatusLabel}</StatusChip>
                      <StatusChip aria-live="polite" className={saveStateClassName} role="status">
                        {saveStateLabel}
                      </StatusChip>
                    </ControlBarMeta>
                    <ControlBarActions>
                      {profile ? (
                        <QuietDeleteButton disabled={isMutating} onClick={() => setIsDeleteModalShowing(true)} type="button">
                          Delete
                        </QuietDeleteButton>
                      ) : null}
                      <Button
                        disabled={isMutating || isUploadingLogo || !!activeLogoFileError || !!uploadedLogoError}
                        type="submit"
                      >
                        Save Changes
                      </Button>
                    </ControlBarActions>
                  </ControlBar>
                ) : null}
                <BrandingContent className={canManageOrganizationBranding ? undefined : 'preview-only'}>
                  {canManageOrganizationBranding ? (
                    <BrandingFormPane>
                      <SectionTitle>Brand settings</SectionTitle>
                      <FieldStack>
                        <FieldWithLabel
                          label="Friendly organization name"
                          error={errors.friendlyOrganizationName}
                          touched={touched.friendlyOrganizationName}
                        >
                          <Input
                            name="friendlyOrganizationName"
                            value={values.friendlyOrganizationName}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        </FieldWithLabel>
                        <FieldWithLabel
                          label="Email footer text (optional)"
                          error={errors.emailFooterText}
                          touched={touched.emailFooterText}
                        >
                          <TextArea
                            name="emailFooterText"
                            value={values.emailFooterText}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        </FieldWithLabel>
                        <LogoPanel aria-labelledby="email-header-logo-label" role="group">
                          <LogoPanelHeader>
                            <LogoLabel id="email-header-logo-label">Email header logo</LogoLabel>
                            <LogoFormat>PNG or JPEG, 2 MB max</LogoFormat>
                          </LogoPanelHeader>
                          <LogoPreview>
                            {logoPreviewUrl ? (
                              <img alt="Email header logo preview" src={logoPreviewUrl} />
                            ) : (
                              <LogoPlaceholder>{activeSelectedLogoFileName || 'No logo selected'}</LogoPlaceholder>
                            )}
                          </LogoPreview>
                          <LogoPanelFooter>
                            <LogoHelpText>{isUploadingLogo ? 'Uploading logo...' : logoFileLabel}</LogoHelpText>
                            <FilePicker>
                              {logoActionLabel}
                              <FileInput
                                key={profileLogoKey || 'empty-logo'}
                                accept={ACCEPTED_LOGO_FILE_TYPES}
                                name="emailHeaderLogoFile"
                                onChange={handleLogoFileChange}
                                type="file"
                              />
                            </FilePicker>
                          </LogoPanelFooter>
                          {activeSelectedLogoFileName ? <SelectedFile>{activeSelectedLogoFileName}</SelectedFile> : null}
                          {uploadedLogo ? <SelectedFile>Logo ready for preview</SelectedFile> : null}
                          {activeLogoFileError ? <ValidationError>{activeLogoFileError}</ValidationError> : null}
                          {uploadedLogoError ? <ValidationError>{uploadedLogoError}</ValidationError> : null}
                        </LogoPanel>
                      </FieldStack>
                    </BrandingFormPane>
                  ) : null}
                  <BrandingPreviewPane>
                    {canManageOrganizationBranding ? (
                      <OrganizationBrandingEmailPreview
                        activeLogoFileError={activeLogoFileError}
                        emailPreview={emailPreview}
                        emailPreviewError={emailPreviewError || ''}
                        hasFetched={hasFetched}
                        isPreviewingEmail={isPreviewingEmail}
                        isUploadingLogo={isUploadingLogo}
                        organizationId={organizationId}
                        profile={profile}
                        uploadedLogo={uploadedLogo}
                        uploadedLogoError={uploadedLogoError || ''}
                        values={values}
                      />
                    ) : (
                      <OrganizationBrandingReadOnlySummary
                        emailFooterText={readOnlyFooterText}
                        friendlyOrganizationName={readOnlyFriendlyName}
                        logoUrl={readOnlyLogoUrl}
                      />
                    )}
                  </BrandingPreviewPane>
                </BrandingContent>
              </RepositCard>
            </form>
          );
        }}
      </Formik>
    </PageShell>
  );
};

export default OrganizationBranding;
